/**
 * Money formatting — the house minor-unit conventions (Plan 02 D1).
 *
 * The payment plane's wire rule (06 §6): money travels as STRING DECIMALS
 * ("29.90"), never floats, and arithmetic happens in minor units (integer
 * cents). These helpers are the client-side half of that contract — the
 * server-side originals live in `server/lib/payment-api/serialize.ts`.
 */

/** Integer cents → the string-decimal the wire fixes ("2990" → "29.90"). */
export function minorToDecimalString(minor: number): string {
  return (minor / 100).toFixed(2);
}

/** String-decimal → integer cents ("29.90" → 2990). Fractional cents round. */
export function decimalStringToMinor(value: string): number {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100);
}

/**
 * Present a string-decimal in a currency. The suite's catalogs carry USD as
 * the display default with TRY/EUR variants; an unknown currency falls back
 * to the plain "9.99" the wire carries. Rendering only — never arithmetic.
 */
export function formatMoney(decimal: string | null | undefined, currency?: string): string {
  if (!decimal) return "—";
  const amount = Number.parseFloat(decimal);
  if (!Number.isFinite(amount)) return decimal;
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: currency && currency.length === 3 ? currency : "USD",
    }).format(amount);
  } catch {
    return `${decimal}${currency ? ` ${currency}` : ""}`;
  }
}
